import { EditorState, Command } from "prosemirror-state";
import {
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { RichTextEditorState, RichTextEditorStateKind } from "../types";

export type RichTextEditorToolProps = {
	value: RichTextEditorState;
	onChange(newState: RichTextEditorState, isSignificant: boolean): void;
	onFocusInput: (() => void) | "NONE";
};

export type ProseMirrorRichTextEditorToolHandlers = {
	editorState: EditorState;
	canApplyCommand(command: Command): boolean;
	applyCommand(command: Command, isSignificant?: boolean): void;
	applyCommandSequence(commands: readonly Command[], isSignificant?: boolean): void;
	focus(): void;
};

export function useProseMirrorRichTextEditorTool(toolProps: RichTextEditorToolProps): ProseMirrorRichTextEditorToolHandlers {
	const {
		value,
		onChange,
		onFocusInput
	} = toolProps;

	if (value.kind !== RichTextEditorStateKind.ProseMirror) {
		throw new Error("This component only works with prosemirror state");
	}

	const {
		editorState
	} = value;

	const editorStateRef: RefObject<EditorState> = useRef<EditorState>(null!);
	editorStateRef.current = editorState;

	const canApplyCommand = useCallback(
		(command: Command): boolean => {
			return command(editorState);
		},
		[editorState]
	);

	const applyCommand = useCallback(
		(command: Command, isSignificant: boolean = false): void => {
			command(
				editorStateRef.current,
				(tr) => {
					const newState: EditorState = editorStateRef.current
						.apply(tr);

					editorStateRef.current = newState;
					onChange(
						{
							kind: RichTextEditorStateKind.ProseMirror,
							editorState: newState,
						},
						isSignificant
					);
				}
			);
		},
		[onChange]
	);

	const applyCommandSequence = useCallback(
		(commands: readonly Command[], isSignificant: boolean = false): void => {
			let iterationState: EditorState = editorState;

			for (const command of commands) {
				const result: boolean = command(
					iterationState,
					//NOTE: It is safe to update iterationState from the callback, because it is called synchronously
					// eslint-disable-next-line @typescript-eslint/no-loop-func
					(tr) => {
						iterationState = iterationState.apply(tr);
					}
				);

				if (!result) {
					return;
				}
			}

			onChange(
				{
				kind: RichTextEditorStateKind.ProseMirror,
				editorState: iterationState
				},
				isSignificant
			);
		},
		[editorState, onChange]
	);

	const focusInputHandler = useCallback(
		(): void => {
			if (onFocusInput !== "NONE") {
				onFocusInput();
			}
		},
		[onFocusInput]
	);

	return useMemo(
		(): ProseMirrorRichTextEditorToolHandlers => {
			return {
				editorState,
				applyCommand,
				applyCommandSequence,
				focus: focusInputHandler,
				canApplyCommand
			};
		},
		[
			applyCommand,
			applyCommandSequence,
			editorState,
			focusInputHandler,
			canApplyCommand
		]
	);
}